import { useCallback } from 'react';
import { useLocalSearchParams } from 'expo-router';

import { useSelector } from '@xstate/react';

import { useGlobalAppState } from '../../../../context/GlobalAppState';
import { useBagIds } from '../../../../hooks';
import { useReorderNavigation } from '../../../../hooks/useReorderNavigation';
import { logger } from '../utils';

export const useCancelReorder = () => {
  const { reorderMachineRef } = useGlobalAppState();
  const state = useSelector(reorderMachineRef, (snapshot) => snapshot);
  const { bagRestaurantSlug, bagAddressId } = useBagIds();
  const { dismissReorder, dismissReorderNewMenu, dismissReorderHome, dismissReorderToMenu } =
    useReorderNavigation();

  const { referrer } = useLocalSearchParams<{ referrer?: string }>();

  return useCallback(
    (options?: CancelReorderOptions) => {
      // ─── State Machine ──────────────────────────────────────────
      reorderMachineRef.send({ type: 'CANCEL_REORDER' });

      // ────────────────────────────────────────────────────────────

      if (options?.dismissToHome) {
        logger.info('Cancel Reorder to Home');
        dismissReorderHome();

        return;
      }

      if (options?.dismissToBagMenu) {
        logger.info('Cancel Reorder to Bag Menu');
        dismissReorderNewMenu(bagRestaurantSlug, bagAddressId);

        return;
      }

      if (options?.dismissToSelectedMenu) {
        logger.info('Cancel Reorder to Selected Menu');
        dismissReorderNewMenu(
          state.context.restaurantSlug,
          state.context.deliveryOrderDetail?.addressId,
        );

        return;
      }

      // ─── Dismiss Back To Menu ────────────────────────────

      if (referrer === 'menu') {
        logger.info('Cancel Reorder to Menu');
        dismissReorderToMenu(bagRestaurantSlug, bagAddressId);

        return;
      }

      // ─── Dismiss without extra navigation ───────────────────────
      logger.info('Cancel Reorder without extra navigation');

      dismissReorder();
    },
    [
      reorderMachineRef,
      referrer,
      dismissReorderToMenu,
      dismissReorder,
      dismissReorderHome,
      dismissReorderNewMenu,
      bagRestaurantSlug,
      bagAddressId,
      state.context.restaurantSlug,
      state.context.deliveryOrderDetail?.addressId,
    ],
  );
};

type CancelReorderOptions = Readonly<{
  dismissToSelectedMenu?: boolean;
  dismissToBagMenu?: boolean;
  dismissToHome?: boolean;
}>;
